/**
 * Data refresh model — pure helpers for the Organizations admin pages.
 *
 * Schedule parsing/serialising, next-run computation, run windows, history
 * grouping, trigger labels and the per-org refresh status shown in the list
 * and on the Data refresh tab. No React, no network: everything here is
 * covered by refreshModel.test.js.
 */

export const DEFAULT_REFRESH_TIME = '09:30';
export const DEFAULT_TIMEZONE = 'America/Chicago';
export const DEFAULT_DAYS_BACK = 30;
export const DEFAULT_DAYS_FORWARD = 365;
export const PROGRESS_POLL_MS = 3000;

export const FACTS = [
  { key: 'dashboardDaily', label: 'Dashboard daily', short: 'Dashboard', table: 'dashboardDailyFact', area: 'dashboards' },
  { key: 'financialMonthly', label: 'Financial monthly', short: 'Financial', table: 'reportFinancialMonthlyFact', area: 'P&L and financial reports' },
  { key: 'laborDaily', label: 'Labor daily', short: 'Labor', table: 'reportLaborDailyFact', area: 'labor reports' },
  { key: 'revenueClassificationDaily', label: 'Revenue classification daily', short: 'Revenue classification', table: 'reportRevenueClassificationDailyFact', area: 'revenue reports' },
];
export const FACT_COUNT = FACTS.length;
const FACT_ORDER = FACTS.reduce((acc, f, i) => ({ ...acc, [f.key]: i }), {});

export const factLabel = (key) => FACTS.find((f) => f.key === key)?.label || key;

// Labor + revenue classification come from one proc, so they fail and retry
// together; name the pair "Labor" in short copy.
export const shortFactNames = (keys = []) => {
  const set = new Set(keys);
  const names = [];
  FACTS.forEach((f) => {
    if (!set.has(f.key)) return;
    if (f.key === 'revenueClassificationDaily' && set.has('laborDaily')) return;
    names.push(f.short);
  });
  return names;
};

export const INTERVAL_OPTIONS = [
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 360, label: '6 hours' },
  { value: 720, label: '12 hours' },
];

export const WINDOW_UNITS = [
  { value: 'days', label: 'Days' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

// ── schedule parsing ────────────────────────────────────────────────────────

export const normalizeTime = (value) => {
  const match = String(value ?? '').trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

/** Array / JSON-string / CSV → sorted unique HH:mm list. */
export const parseRefreshTimes = (value, fallbackValue) => {
  let raw = value;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try { raw = JSON.parse(trimmed); } catch (_) { raw = trimmed; }
    }
  }
  if (!Array.isArray(raw)) {
    raw = String(raw || fallbackValue || DEFAULT_REFRESH_TIME).split(',');
  }
  const times = raw.map(normalizeTime).filter(Boolean);
  const unique = [...new Set(times)].sort();
  if (unique.length) return unique;
  const fb = normalizeTime(fallbackValue);
  return fb ? [fb] : [DEFAULT_REFRESH_TIME];
};

export const isValidTimezone = (value) => {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value.trim() }).format(new Date());
    return true;
  } catch (_) {
    return false;
  }
};

export const normalizeTimezone = (value, fallback = DEFAULT_TIMEZONE) => {
  let text = value;
  if (value && typeof value === 'object') text = value.value || value.id || value.name || value.label;
  text = typeof text === 'string' ? text.trim() : '';
  return isValidTimezone(text) ? text : fallback;
};

const toInt = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  const n = Number(value);
  return Number.isInteger(n) ? n : NaN;
};

export const normalizeDaysBack = (value) => {
  const n = toInt(value);
  return Number.isInteger(n) && n >= 1 ? n : DEFAULT_DAYS_BACK;
};

export const normalizeDaysForward = (value) => {
  const n = toInt(value);
  return Number.isInteger(n) && n >= 0 ? n : DEFAULT_DAYS_FORWARD;
};

export const isScheduleEnabled = (org) => org?.dashboardRefreshEnabled !== false && org?.dashboardRefreshEnabled !== 0;

export const intervalMinutesOf = (org) => {
  const n = Number.parseInt(org?.dashboardRefreshIntervalMinutes, 10);
  return Number.isInteger(n) && n >= 10 ? n : null;
};

/** The org's saved schedule in editor shape. */
export const scheduleFromOrg = (org) => {
  const interval = intervalMinutesOf(org);
  return {
    enabled: isScheduleEnabled(org),
    mode: interval ? 'interval' : 'times',
    times: parseRefreshTimes(org?.dashboardRefreshTimesLocal, org?.dashboardRefreshTimeLocal),
    intervalMinutes: interval || 60,
    timezone: normalizeTimezone(org?.dashboardRefreshTimezone),
    daysBack: normalizeDaysBack(org?.dashboardRefreshDaysBack),
    daysForward: normalizeDaysForward(org?.dashboardRefreshDaysForward),
  };
};

export const validateSchedule = (draft) => {
  const errors = {};
  if (draft.mode === 'times') {
    const times = (draft.times || []).map(normalizeTime).filter(Boolean);
    if (!times.length) errors.times = 'Add at least one run time.';
  } else if (!INTERVAL_OPTIONS.some((o) => o.value === Number(draft.intervalMinutes))) {
    errors.intervalMinutes = 'Pick an interval.';
  }
  if (!isValidTimezone(draft.timezone)) errors.timezone = 'Pick a timezone.';
  const back = toInt(draft.daysBack);
  if (!Number.isInteger(back) || back < 1 || back > 3650) errors.daysBack = 'Days back must be 1–3650.';
  const fwd = toInt(draft.daysForward);
  if (!Number.isInteger(fwd) || fwd < 0 || fwd > 3650) errors.daysForward = 'Days forward must be 0–3650.';
  return errors;
};

/**
 * Only the schedule columns that changed, ready for PUT /organizations.
 * Never includes name/code/etc., so a schedule save can't clobber settings.
 */
export const schedulePatch = (original, draft) => {
  const out = {};
  if (Boolean(draft.enabled) !== Boolean(original.enabled)) out.dashboardRefreshEnabled = Boolean(draft.enabled);
  const origInterval = original.mode === 'interval' ? Number(original.intervalMinutes) : null;
  const nextInterval = draft.mode === 'interval' ? Number(draft.intervalMinutes) : null;
  if (origInterval !== nextInterval) out.dashboardRefreshIntervalMinutes = nextInterval;
  const nextTimes = parseRefreshTimes(draft.times);
  if (nextTimes.join(',') !== parseRefreshTimes(original.times).join(',')) out.dashboardRefreshTimesLocal = nextTimes;
  if (draft.timezone !== original.timezone) out.dashboardRefreshTimezone = draft.timezone;
  if (toInt(draft.daysBack) !== Number(original.daysBack)) out.dashboardRefreshDaysBack = toInt(draft.daysBack);
  if (toInt(draft.daysForward) !== Number(original.daysForward)) out.dashboardRefreshDaysForward = toInt(draft.daysForward);
  return out;
};

export const intervalLabel = (minutes) => {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return '—';
  if (m === 60) return 'Every hour';
  if (m > 60 && m % 60 === 0) return `Every ${m / 60} hours`;
  return `Every ${m} min`;
};

// ── time zone formatting ────────────────────────────────────────────────────

const dtfCache = new Map();
const partsFormatter = (tz) => {
  if (!dtfCache.has(tz)) {
    dtfCache.set(tz, new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }));
  }
  return dtfCache.get(tz);
};

const toDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
};

/** Wall-clock parts of `date` in `tz`. */
export const zonedParts = (value, tz = DEFAULT_TIMEZONE) => {
  const date = toDate(value);
  if (!date) return null;
  const zone = isValidTimezone(tz) ? tz : DEFAULT_TIMEZONE;
  const parts = partsFormatter(zone).formatToParts(date).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  const hour = Number(parts.hour) % 24;
  const minute = Number(parts.minute);
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour,
    minute,
    minuteOfDay: hour * 60 + minute,
    dateKey: `${parts.year}-${parts.month}-${parts.day}`,
  };
};

const pad2 = (n) => String(n).padStart(2, '0');
const minutesToClock = (m) => `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
const clockToMinutes = (t) => {
  const n = normalizeTime(t);
  if (!n) return null;
  const [h, m] = n.split(':').map(Number);
  return h * 60 + m;
};

export const formatClock = (value, tz) => {
  const p = zonedParts(value, tz);
  return p ? `${pad2(p.hour)}:${pad2(p.minute)}` : '—';
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatShortDate = (value, tz) => {
  const p = zonedParts(value, tz);
  return p ? `${MONTHS[p.month - 1]} ${p.day}` : '—';
};

export const formatDateTime = (value, tz) => {
  const p = zonedParts(value, tz);
  return p ? `${MONTHS[p.month - 1]} ${p.day} ${pad2(p.hour)}:${pad2(p.minute)}` : '—';
};

export const isSameZonedDay = (a, b, tz) => {
  const pa = zonedParts(a, tz);
  const pb = zonedParts(b, tz);
  return Boolean(pa && pb && pa.dateKey === pb.dateKey);
};

/** "06:31" today, "Yesterday 06:31", "Sep 14 06:31". */
export const formatWhen = (value, tz, now = new Date()) => {
  const p = zonedParts(value, tz);
  if (!p) return '—';
  const clock = `${pad2(p.hour)}:${pad2(p.minute)}`;
  if (isSameZonedDay(value, now, tz)) return clock;
  const yesterday = new Date(toDate(now).getTime() - 24 * 60 * 60 * 1000);
  if (isSameZonedDay(value, yesterday, tz)) return `Yesterday ${clock}`;
  return `${MONTHS[p.month - 1]} ${p.day} ${clock}`;
};

/** Next scheduled run, from the saved schedule. */
export const computeNextRun = (org, now = new Date()) => {
  const s = scheduleFromOrg(org);
  if (!s.enabled) return { mode: 'off', label: '—' };
  if (s.mode === 'interval') return { mode: 'interval', label: intervalLabel(s.intervalMinutes) };
  const p = zonedParts(now, s.timezone);
  const mins = s.times.map(clockToMinutes).filter((m) => m !== null).sort((a, b) => a - b);
  if (!p || !mins.length) return { mode: 'times', label: '—' };
  const next = mins.find((m) => m > p.minuteOfDay);
  if (next !== undefined) return { mode: 'times', day: 'today', time: minutesToClock(next), label: `Today ${minutesToClock(next)}` };
  return { mode: 'times', day: 'tomorrow', time: minutesToClock(mins[0]), label: `Tomorrow ${minutesToClock(mins[0])}` };
};

export const scheduleSummary = (org) => {
  const s = scheduleFromOrg(org);
  if (!s.enabled) return 'Off';
  if (s.mode === 'interval') return `${intervalLabel(s.intervalMinutes)} · ${s.timezone}`;
  return `${s.times.join(', ')} · ${s.timezone}`;
};

// ── durations ───────────────────────────────────────────────────────────────

/** 45s · 3m 21s · 1h 04m */
export const formatElapsed = (ms) => {
  const safe = Number.isFinite(ms) && ms > 0 ? ms : 0;
  const totalSec = Math.floor(safe / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  if (min < 60) return `${min}m ${pad2(totalSec % 60)}s`;
  return `${Math.floor(min / 60)}h ${pad2(min % 60)}m`;
};

export const formatDuration = (ms) => {
  if (ms === null || ms === undefined || !Number.isFinite(Number(ms))) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return formatElapsed(ms);
};

export const formatCount = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('en-US') : '—');

// ── run-now window ──────────────────────────────────────────────────────────

const todayKey = (now = new Date()) => `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
const parseDateKey = (key) => new Date(`${key}T00:00:00`);
const addDays = (date, days) => { const d = new Date(date); d.setDate(d.getDate() + days); return d; };
const addMonths = (date, months) => { const d = new Date(date); d.setMonth(d.getMonth() + months); return d; };
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const unitMonths = (value, unit) => {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n)) return 0;
  return unit === 'years' ? n * 12 : n;
};

export const defaultRunForm = (org) => {
  const s = scheduleFromOrg(org);
  return {
    mode: 'default',
    backValue: s.daysBack,
    backUnit: 'days',
    fwdValue: s.daysForward,
    fwdUnit: 'days',
    locationIds: [],
  };
};

/** Same date math the refresh procs are asked for (kept from the old modal). */
export const computeRefreshWindow = ({ backValue, backUnit = 'days', fwdValue, fwdUnit = 'days', asOfDate } = {}, now = new Date()) => {
  const asOf = parseDateKey(asOfDate || todayKey(now));
  const back = Number.parseInt(backValue, 10);
  const fwd = Number.parseInt(fwdValue, 10);
  const backward = Number.isFinite(back) ? back : 1;
  const forward = Number.isFinite(fwd) ? fwd : 0;
  const startDate = backUnit === 'days'
    ? addDays(asOf, -(Math.max(backward, 1) - 1))
    : startOfMonth(addMonths(asOf, -Math.max(unitMonths(backward, backUnit), 1)));
  const endDate = fwdUnit === 'days'
    ? addDays(asOf, Math.max(forward, 0))
    : endOfMonth(addMonths(asOf, Math.max(unitMonths(forward, fwdUnit), 0)));
  return { startDate, endDate };
};

export const buildRefreshPayload = ({ backValue, backUnit = 'days', fwdValue, fwdUnit = 'days' }) => {
  const back = Number.parseInt(backValue, 10);
  const fwd = Number.parseInt(fwdValue, 10);
  return {
    historyMonths: backUnit === 'days' ? 1 : Math.max(unitMonths(back, backUnit), 1),
    futureMonths: fwdUnit === 'days' ? 0 : Math.max(unitMonths(fwd, fwdUnit), 0),
    historyDays: backUnit === 'days' ? Math.max(back, 1) : null,
    futureDays: fwdUnit === 'days' ? Math.max(fwd, 0) : null,
  };
};

export const validateRunForm = (form) => {
  if (form.mode !== 'custom') return {};
  const errors = {};
  const back = Number.parseInt(form.backValue, 10);
  const fwd = Number.parseInt(form.fwdValue, 10);
  if (!Number.isFinite(back) || back < 1) errors.backValue = 'Enter 1 or more.';
  if (!Number.isFinite(fwd) || fwd < 0) errors.fwdValue = 'Enter 0 or more.';
  return errors;
};

/** POST /factRefresh/run body for a Run-now form. */
export const buildRunRequest = (org, form, { facts } = {}) => {
  const organizationId = org.id;
  if (form.mode === 'full') return { organizationId, fullBackfill: true };
  const s = scheduleFromOrg(org);
  const window = form.mode === 'custom'
    ? form
    : { backValue: s.daysBack, backUnit: 'days', fwdValue: s.daysForward, fwdUnit: 'days' };
  const body = { organizationId, ...buildRefreshPayload(window) };
  if (Array.isArray(form.locationIds) && form.locationIds.length) body.locationIds = form.locationIds;
  if (Array.isArray(facts) && facts.length) body.facts = facts;
  return body;
};

const DATE_FMT = { month: 'short', day: 'numeric', year: 'numeric' };
export const formatLongDate = (date) => new Intl.DateTimeFormat('en-US', DATE_FMT).format(date);

/** "Aug 24, 2026 → Sep 23, 2027" for the Covers line. */
export const coverageLabel = (org, form, now = new Date()) => {
  if (form.mode === 'full') {
    const asOf = parseDateKey(todayKey(now));
    const start = startOfMonth(addMonths(asOf, -25));
    const end = endOfMonth(addMonths(asOf, 13));
    return `${formatLongDate(start)} → ${formatLongDate(end)}`;
  }
  const s = scheduleFromOrg(org);
  const window = form.mode === 'custom'
    ? form
    : { backValue: s.daysBack, backUnit: 'days', fwdValue: s.daysForward, fwdUnit: 'days' };
  const { startDate, endDate } = computeRefreshWindow(window, now);
  return `${formatLongDate(startDate)} → ${formatLongDate(endDate)}`;
};

/** "30 days back · 365 days forward" from the saved schedule (bug G: real values). */
export const scheduleWindowLabel = (org) => {
  const s = scheduleFromOrg(org);
  const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
  return `${plural(s.daysBack, 'day')} back · ${plural(s.daysForward, 'day')} forward`;
};

// ── triggers ────────────────────────────────────────────────────────────────

export const TRIGGERS = {
  schedule: { label: 'Schedule', word: 'scheduled' },
  interval: { label: 'Interval', word: 'interval' },
  user: { label: 'Manual', word: 'manual' },
  etl: { label: 'Data load', word: 'data-load' },
  backfill: { label: 'Full rebuild', word: 'full rebuild' },
};

export const shortUser = (email) => {
  if (!email) return '';
  const text = String(email);
  return text.includes('@') ? text.split('@')[0] : text;
};

export const triggerLabel = (source, by) => {
  if (!source) return '—';
  const meta = TRIGGERS[source];
  if (!meta) return source;
  if (source === 'user' && by) return `${meta.label} · ${shortUser(by)}`;
  return meta.label;
};

// ── history grouping ────────────────────────────────────────────────────────

const GROUP_GAP_MS = 3 * 60 * 1000;
const MULTI_LOCATION_TRIGGERS = new Set(['user', 'backfill']);
const SELF_DESCRIBING_TRIGGERS = new Set(['user', 'etl']); // may legitimately run a subset of tables

const daysBetween = (fromKey, toKey) => {
  const a = parseDateKey(fromKey);
  const b = parseDateKey(toKey);
  return Math.round((b - a) / (24 * 60 * 60 * 1000));
};

/**
 * Group per-table history rows into runs. A row joins a run when it has the
 * same trigger (and admin), starts within a few minutes of the run's last
 * table finishing, and doesn't repeat a table the run already wrote. Rows of
 * one run share a location, except manual runs over a subset of locations.
 */
export const groupHistoryRuns = (rows = [], tz = DEFAULT_TIMEZONE) => {
  const items = rows
    .map((r) => {
      const t = toDate(r.startedAt)?.getTime();
      if (!Number.isFinite(t)) return null;
      return {
        ...r,
        t,
        end: t + (Number(r.durationMs) || 0),
        locationId: r.locationId ?? null,
        trigger: r.triggerSource || null,
        by: r.triggeredBy || null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.t - b.t) || (Number(a.id) - Number(b.id)));

  const groups = [];
  items.forEach((row) => {
    const key = `${row.factName}|${row.locationId}`;
    let target = null;
    for (let i = groups.length - 1; i >= Math.max(0, groups.length - 4); i -= 1) {
      const g = groups[i];
      if (g.trigger !== row.trigger || g.by !== row.by) continue;
      if (row.t > g.end + GROUP_GAP_MS) continue;
      if (g.keys.has(key)) continue;
      const sameScope = g.locationKey === row.locationId
        || (MULTI_LOCATION_TRIGGERS.has(row.trigger) && g.locationKey !== null && row.locationId !== null);
      if (!sameScope) continue;
      target = g;
      break;
    }
    if (!target) {
      target = { trigger: row.trigger, by: row.by, locationKey: row.locationId, start: row.t, end: row.end, keys: new Set(), rows: [] };
      groups.push(target);
    }
    target.rows.push(row);
    target.keys.add(key);
    target.end = Math.max(target.end, row.end);
  });

  return groups.map((g) => {
    const sortedRows = [...g.rows].sort((a, b) => (
      (Number(a.locationId) || 0) - (Number(b.locationId) || 0)
      || (FACT_ORDER[a.factName] ?? 9) - (FACT_ORDER[b.factName] ?? 9)
    ));
    const locationIds = [...new Set(sortedRows.map((r) => r.locationId).filter((v) => v !== null))];
    const succeededRows = sortedRows.filter((r) => r.status === 'succeeded');
    const failedRows = sortedRows.filter((r) => r.status !== 'succeeded');
    const orgWide = locationIds.length === 0;
    const expected = SELF_DESCRIBING_TRIGGERS.has(g.trigger)
      ? sortedRows.length
      : FACT_COUNT * Math.max(1, locationIds.length);
    const presentFacts = new Set(sortedRows.map((r) => r.factName));
    const missingFacts = SELF_DESCRIBING_TRIGGERS.has(g.trigger) || !orgWide
      ? []
      : FACTS.map((f) => f.key).filter((k) => !presentFacts.has(k));
    const failedFacts = [...new Set([...failedRows.map((r) => r.factName), ...missingFacts])];
    let result = 'done';
    if (failedRows.length || succeededRows.length < expected) result = succeededRows.length ? 'partial' : 'failed';
    const errorText = failedRows.find((r) => r.error)?.error
      || (missingFacts.length ? `Not refreshed: ${missingFacts.map(factLabel).join(', ')}` : null);
    const windowRow = sortedRows.find((r) => r.factName === 'dashboardDaily' && r.windowStart) || sortedRows.find((r) => r.windowStart);
    return {
      id: `run-${sortedRows[0].id}`,
      startedAt: new Date(g.start),
      endedAt: new Date(g.end),
      durationMs: g.end - g.start,
      rows: sortedRows,
      trigger: g.trigger,
      triggeredBy: g.by,
      locationIds,
      orgWide,
      tables: succeededRows.length,
      expected,
      failed: failedRows.length,
      failedFacts,
      missingFacts,
      inserted: sortedRows.reduce((n, r) => n + (Number(r.rowsInserted) || 0), 0),
      deleted: sortedRows.reduce((n, r) => n + (Number(r.rowsDeleted) || 0), 0),
      windowStart: windowRow?.windowStart || null,
      windowEnd: windowRow?.windowEnd || null,
      result,
      errorText,
      runDateKey: zonedParts(new Date(g.start), tz)?.dateKey || null,
    };
  }).sort((a, b) => b.startedAt - a.startedAt);
};

export const describeRunWindow = (run) => {
  if (!run) return '—';
  const scope = run.orgWide ? '' : ` · ${run.locationIds.length} location${run.locationIds.length === 1 ? '' : 's'}`;
  if (run.trigger === 'backfill') return `Full rebuild${scope}`;
  if (!run.windowStart || !run.windowEnd || !run.runDateKey) return scope ? scope.slice(3) : '—';
  if (run.windowStart === run.windowEnd) {
    const [, m, d] = run.windowStart.split('-').map(Number);
    return `${MONTHS[m - 1]} ${d}${scope}`;
  }
  const back = Math.max(0, daysBetween(run.windowStart, run.runDateKey));
  const fwd = Math.max(0, daysBetween(run.runDateKey, run.windowEnd));
  return `${back}d back · ${fwd}d fwd${scope}`;
};

export const RESULT_META = {
  done: { tone: 'ok', label: 'Done' },
  partial: { tone: 'bad', label: 'Partly failed' },
  failed: { tone: 'bad', label: 'Failed' },
  running: { tone: 'info', label: 'Running' },
};

// ── per-org refresh status ──────────────────────────────────────────────────

const isTimeout = (text) => /time[d]?\s?out|timeout/i.test(String(text || ''));

export const shortFailure = (failedFacts = [], errorText = '') => {
  const names = shortFactNames(failedFacts);
  const verb = isTimeout(errorText) ? 'timed out' : 'failed';
  if (!names.length) return isTimeout(errorText) ? 'Refresh timed out' : 'Refresh failed';
  return `${names.join(' & ')} ${names.length === 1 ? 'fact ' : ''}${verb}`;
};

/** Parse "laborDaily+revenueClassificationDaily (location 7): msg; …" facts. */
export const factsFromErrorText = (text) => {
  const found = new Set();
  String(text || '').split(';').forEach((part) => {
    const head = part.split(':')[0];
    head.split(/[+\s(]/).forEach((token) => {
      const t = token.trim();
      if (FACT_ORDER[t] !== undefined) found.add(t);
    });
  });
  return FACTS.map((f) => f.key).filter((k) => found.has(k));
};

const maxDate = (...values) => values.map(toDate).filter(Boolean).sort((a, b) => b - a)[0] || null;

/**
 * Everything the list row and the Data refresh tab say about an org's refresh.
 *   liveStatus: optional GET /factRefresh/status response for this org
 */
export const deriveRefreshState = (org, { now = new Date(), liveStatus = null } = {}) => {
  const tz = normalizeTimezone(org?.dashboardRefreshTimezone);
  const enabled = isScheduleEnabled(org);
  const runs = groupHistoryRuns(org?.recentRuns || [], tz);
  const latest = runs[0] || null;
  const status = String(org?.dashboardRefreshLastStatus || '').toLowerCase();
  const lastRunAt = toDate(org?.dashboardRefreshLastRunAt);
  const lastSuccess = maxDate(org?.lastSuccessAt, status === 'succeeded' ? lastRunAt : null);
  const refreshedToday = Boolean(lastSuccess && isSameZonedDay(lastSuccess, now, tz));

  const inFlight = liveStatus
    ? (liveStatus.running ? liveStatus : null)
    : (org?.inFlight?.running ? org.inFlight : null);
  const base = { tz, enabled, runs, latest, lastRunAt, lastSuccess, refreshedToday, failedFacts: [], errorText: null, failedAt: null };

  if (inFlight) {
    const done = Number(inFlight.tablesDone) || 0;
    const total = Number(inFlight.tablesTotal) || FACT_COUNT;
    return {
      ...base,
      key: 'running',
      tone: 'info',
      label: 'Running',
      running: inFlight,
      detail: `Started ${formatClock(inFlight.startedAt, tz)} · ${done} of ${total} table${total === 1 ? '' : 's'}`,
    };
  }

  const failedStates = (org?.factStates || []).filter((s) => String(s.lastStatus).toLowerCase() === 'failed');
  const orgError = status === 'failed' || status === 'partial' ? org?.dashboardRefreshLastError : null;
  const latestFailedFacts = latest && latest.result !== 'done' ? latest.failedFacts : [];
  const failedFacts = [...new Set([
    ...factsFromErrorText(orgError),
    ...failedStates.map((s) => s.factName),
    ...latestFailedFacts,
  ])].sort((a, b) => FACT_ORDER[a] - FACT_ORDER[b]);
  const errorText = orgError
    || failedStates.find((s) => s.lastError)?.lastError
    || (latest && latest.result !== 'done' ? latest.errorText : null)
    || null;
  const failedAt = maxDate(
    status === 'failed' || status === 'partial' ? lastRunAt : null,
    ...failedStates.map((s) => s.updatedAt),
    latest && latest.result !== 'done' ? latest.endedAt : null,
  );

  const hardFailed = status === 'failed' || (latest && latest.result === 'failed' && status !== 'partial');
  if (hardFailed) {
    return {
      ...base, key: 'failed', tone: 'bad', label: 'Failed', failedFacts, errorText, failedAt,
      detail: `${shortFailure(failedFacts, errorText)} · ${formatWhen(failedAt, tz, now)}`,
    };
  }
  if (status === 'partial' || failedFacts.length || (latest && latest.result !== 'done')) {
    return {
      ...base, key: 'partial', tone: 'bad', label: 'Failed', partial: true, failedFacts, errorText, failedAt,
      detail: `${shortFailure(failedFacts, errorText)} · ${formatWhen(failedAt, tz, now)}`,
    };
  }
  if (!lastRunAt && !latest) {
    return { ...base, key: 'never', tone: 'off', label: enabled ? 'Not run yet' : 'Schedule off', detail: 'No refresh recorded' };
  }
  const lastAny = maxDate(lastRunAt, latest?.endedAt);
  if (!enabled) {
    return { ...base, key: 'off', tone: 'off', label: 'Schedule off', detail: `No set times · last ${formatShortDate(lastAny, tz)}` };
  }
  const tables = latest ? latest.tables : null;
  const tablesText = tables ? ` · ${tables} table${tables === 1 ? '' : 's'}` : '';
  const updated = lastSuccess || lastAny;
  const stale = updated && now - updated > 48 * 60 * 60 * 1000;
  if (stale) {
    return { ...base, key: 'stale', tone: 'warn', label: 'Behind', detail: `Last updated ${formatShortDate(updated, tz)}` };
  }
  return { ...base, key: 'ok', tone: 'ok', label: 'Up to date', detail: `Updated ${formatWhen(updated, tz, now)}${tablesText}` };
};

/** Plain-language consequence of a failed / partly failed refresh. */
export const failureMeaning = (state, org) => {
  const facts = state.failedFacts || [];
  const tz = state.tz;
  if (!facts.length || facts.length >= FACT_COUNT) {
    const last = state.lastSuccess ? ` (${formatDateTime(state.lastSuccess, tz)})` : '';
    return `Dashboards and reports still show data from the last successful refresh${last}.`;
  }
  const others = FACT_COUNT - facts.length;
  const areas = [...new Set(facts.map((k) => FACTS.find((f) => f.key === k)?.area).filter(Boolean))];
  const states = (org?.factStates || []).filter((s) => facts.includes(s.factName) && s.lastRefreshedAt);
  const asOf = states.length ? formatDateTime(maxDate(...states.map((s) => s.lastRefreshedAt)), tz) : null;
  const areaText = areas.join(' and ');
  return `The other ${others} table${others === 1 ? '' : 's'} refreshed, so ${areaText} show data as of ${asOf || 'their last successful refresh'}.`;
};

/** Facts a "Retry … only" button can target (a strict subset of all four). */
export const retryableFacts = (state) => {
  const facts = state?.failedFacts || [];
  if (!facts.length || facts.length >= FACT_COUNT) return null;
  return facts;
};

/** Banner title for a failed refresh. */
export const failureTitle = (state) => {
  const verb = state.key === 'partial' ? 'partly failed' : 'failed';
  const latest = state.latest;
  const failedRun = latest && latest.result !== 'done' ? latest : null;
  const at = failedRun ? failedRun.startedAt : state.failedAt;
  const word = failedRun?.trigger ? `${TRIGGERS[failedRun.trigger]?.word || ''} ` : '';
  return `The ${formatClock(at, state.tz)} ${word}refresh ${verb}`.replace(/\s+/g, ' ');
};
